import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Briefcase,
  CalendarClock,
  ChartColumn,
  Check,
  ChevronRight,
  Folder,
  CircleDollarSign,
  CalendarDays,
  Plus,
  ArrowDownRight,
  FileSignature,
  FileText,
  Flag,
  GraduationCap,
  Heart,
  KanbanSquare,
  Landmark,
  Lock,
  Mail,
  MapPin,
  MessageSquare,
  SearchCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  TrendingUp,
  UserCheck,
  type LucideIcon,
} from "lucide-react";

import Loader from "@/components/ui/loader";
import { NIVA_AVATAR_POSTER } from "@/components/NivaAvatar";
import { WelcomeHero } from "@/components/buyer-dashboard/DashboardShared";
import { useToast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { canUseNiva, upgradeCtaPath } from "@/lib/access";
import { orgScopedPath } from "@/lib/orgDoor";
import { useAuth } from "@/contexts/AuthContext";
import { useNiva } from "@/contexts/NivaContext";
import { useModuleVisibility } from "@/lib/moduleVisibility";
import { cn } from "@/lib/utils";
import { industryIcon } from "@/lib/buyboxIcons";

/**
 * Buyer dashboard — the page every buyer lands on after sign-in.
 *
 * One request (GET /dashboard/buyer) fills the whole page: the acquisition
 * journey rail, then a grid of module cards (buybox, matches, deal CRM,
 * shortlist, messages, financing, documents, meetings, academy) beside a sticky
 * rail holding the closure forecast, Niva, and the ranked next actions.
 *
 * Cards are accordions — one open at a time. The collapsed face carries the
 * figures; opening one reveals the detail rows and the links into that module,
 * so nothing here is a dead end.
 *
 * Every number comes from the payload. Sections with no data render their own
 * empty state rather than a zero that reads as a bug: `meetings` in particular
 * has no backing model yet and always arrives empty.
 *
 * The scored match ROWS are the one thing loaded on a second request. The BuyBox
 * scoring engine ranks every active listing and takes seconds; blocking the
 * whole landing page on it would make sign-in feel broken, so that one card
 * fills in late from GET /business-listings/deal-scoring — the same endpoint the
 * AI Deal Matching page uses, so there is still exactly one scoring engine.
 */

// ── payload types (mirrors app/services/buyer_dashboard.py) ──────────

export interface JourneyStep {
  no: string;
  key: string;
  title: string;
  note: string;
  status: "complete" | "current" | "ahead";
}

export interface KV { k: string; v: string }

export interface MatchItem {
  listing_id: string;
  reference_code: string | null;
  score: number;
  name: string;
  meta: string;
  // The mobile layout lays these out as a photo card rather than one meta line.
  image_url?: string | null;
  location?: string | null;
  revenue?: string | null;
  asking?: string | null;
  category?: string | null;
  /** Raw asking price, for the web card's "Within your budget" tag. */
  asking_price?: number | null;
}

/** The deferred match card's state — shared with the mobile layout. */
export type MatchRowsState = MatchItem[] | "loading" | "locked" | "error";

/** The slice of GET /business-listings/deal-scoring this page renders. */
interface ScoredDeal {
  listing_id: string;
  reference_code: string | null;
  title: string;
  location: string | null;
  category: string | null;
  listing_image_url?: string | null;
  asking_price: number | null;
  revenue: number | null;
  fit_score: number;
}

export interface PipelineGroup { key: string; label: string; count: number; is_late: boolean }

export interface PipelineDeal {
  listing_id: string;
  name: string;
  reference_code: string | null;
  ev: string;
  stage: string;
  stage_label: string;
}

export interface ShortlistItem {
  listing_id: string;
  name: string;
  reference_code: string | null;
  note: string;
  city?: string | null;
  image_url?: string | null;
  asking_price?: number | null;
  revenue?: string | null;
  category?: string | null;
}

export interface MessageItem {
  interest_id: string;
  listing_id: string;
  who: string;
  listing_title: string | null;
  when: string;
  line: string;
  unread: number;
}

export interface DocItem { name: string; state: "done" | "open" | "todo"; detail: string; to: string }

export interface Course { key: string; name: string; pct: number; done: number; total: number }

export interface Risk { title: string; note: string; level: "ok" | "warn" | "info" }

export interface Access {
  is_basic: boolean;
  plan_name: string | null;
  free_unlocks_total?: number;
  free_unlocks_remaining?: number;
}

export interface DashboardPayload {
  buyer: {
    first_name: string | null;
    week_number: number;
    today_iso: string;
  };
  position: {
    deals_in_pipeline: number;
    live_deals: number;
    ev_live_label: string;
    listings_viewed: number;
    interests_raised: number;
  };
  access: Access;
  journey: { current_step: number; current_label: string; steps: JourneyStep[] };
  buybox: {
    has_preferences: boolean;
    completion: number;
    missing: string[];
    rows: KV[];
    budget_min?: number | null;
    budget_max?: number | null;
    // Structured Buy Box for the card's chip previews (older backends omit them).
    min_sde?: string | null;
    business_age?: string | null;
    sectors?: string[];
    states?: string[];
    cities?: string[];
    countries?: string[];
    locations?: string[];
  };
  // No `items`: the service ships this card's header only, and the scored rows
  // arrive on the second request (see matchRows).
  matches: { has_preferences: boolean; new_this_week: number; locked?: boolean };
  pipeline: { total: number; groups: PipelineGroup[]; deals: PipelineDeal[]; locked?: boolean };
  // `ids` is every saved listing (items is only the first few), so the match
  // rows' hearts start in the right state.
  shortlist: { saved: number; items: ShortlistItem[]; ids?: string[]; locked?: boolean };
  messages: { total: number; awaiting: number; items: MessageItem[]; locked?: boolean };
  financing: {
    prequalified: boolean;
    prequalifications: string[];
    ceiling_label: string | null;
    equity_injection_pct: number;
    cash_required_label: string | null;
    applications: { id: string; reference_code: string; partner_name: string; status: string; status_label: string }[];
    application_count: number;
    deals_needing_funding: number;
    locked?: boolean;
  };
  documents: { needs_signature: number; items: DocItem[] };
  meetings: { items: { when: string; title: string; with: string }[]; available: boolean };
  academy: {
    percent: number;
    lessons_completed: number;
    lessons_total: number;
    courses: Course[];
    resume: Course | null;
    locked?: boolean;
  };
  forecast: {
    has_deal: boolean;
    percent: number;
    deal_name: string | null;
    deal_name_short?: string | null;
    listing_id: string | null;
    stage_label: string | null;
    risks: Risk[];
    stalled: { item: string; listing_id: string; age: string }[];
    locked?: boolean;
  };
  actions: { no: string; text: string; to: string }[];
}

/** Compact currency for the match rows — mirrors money() in
 *  app/services/buyer_dashboard.py so the deferred card reads like the rest. */
function money(n: number | null | undefined): string | null {
  if (!n || n <= 0) return null;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString()}`;
}

// ── small presentational pieces ──────────────────────────────────────

/** The uppercase micro-label that opens every card and column. */
function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("font-sans text-[11px] font-bold uppercase tracking-[0.14em] text-primary", className)}>
      {children}
    </div>
  );
}

/** Serif figure over a small caption — the KPI unit used in the hero and cards. */
function Stat({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div>
      <div className="font-display text-[1.75rem] font-semibold leading-none tabular-nums text-foreground">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

/** One icon per journey stage, in order: signup, buybox, match & NDA, LOI & diligence, close. */
const JOURNEY_ICONS: LucideIcon[] = [UserCheck, Target, FileSignature, SearchCheck, Flag];

/**
 * Where each journey step opens — every step is a link, not just the current
 * one. A step whose module is locked for this buyer opens their upgrade page
 * instead of a wall. Returns an unscoped path (callers run it through `path`),
 * except the upgrade targets, which arrive already scoped.
 */
export function journeyStepTarget(
  key: string,
  data: Pick<DashboardPayload, "messages" | "pipeline" | "financing">,
  plans: string,
  operatorPlans: string,
): { to: string; scoped: boolean } {
  switch (key) {
    case "signup":
    case "buybox":
      return { to: "/buyer/settings/profile", scoped: false };
    case "match":
      // Approaches + NDAs live in the interests inbox; without one yet, the
      // marketplace is where an approach starts.
      return data.messages.locked
        ? { to: plans, scoped: true }
        : { to: data.messages.total > 0 ? "/buyer/interests" : "/buyer/home", scoped: false };
    case "loi":
      return data.pipeline.locked ? { to: operatorPlans, scoped: true } : { to: "/buyer/pipeline", scoped: false };
    case "close":
      return data.financing.locked ? { to: plans, scoped: true } : { to: "/buyer/funding", scoped: false };
    default:
      return { to: "/buyer/home", scoped: false };
  }
}

/** A live-position tile on the hero's always-dark ground. */
function HeroStat({ icon: Icon, value, label }: { icon: LucideIcon; value: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-primary/30 bg-white/[0.06] p-3 backdrop-blur-sm lg:gap-3 lg:p-3.5">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/20 lg:h-10 lg:w-10">
        <Icon className="h-4 w-4 text-primary lg:h-5 lg:w-5" />
      </span>
      <span className="min-w-0">
        <span className="block truncate font-sans text-[1.35rem] font-bold leading-none tabular-nums text-foreground lg:text-[1.75rem]">{value}</span>
        <span className="mt-1 block text-xs font-semibold leading-tight text-foreground lg:mt-1.5 lg:text-[13px]">{label}</span>
      </span>
    </div>
  );
}

/** Tinted icon tiles on the module cards. The -50 surfaces are darkened by the
 *  dark theme's accent-scale overrides in index.css, so these read in both. */
type IconTone = "gold" | "rose" | "sky" | "orange" | "blue" | "violet";
const ICON_TONE: Record<IconTone, string> = {
  gold: "bg-primary/10 text-primary",
  rose: "bg-rose-50 text-rose-500",
  sky: "bg-sky-50 text-sky-600",
  orange: "bg-orange-50 text-orange-500",
  blue: "bg-blue-50 text-blue-500",
  violet: "bg-violet-50 text-violet-500",
};

/** Soft gold-tint card action ("+ Add a listing", "Explore financing").
 *  Text colour sits on the child — `a { color: inherit }` beats text-* on <a>. */
function SoftAction({ to, children, icon: Icon }: { to: string; children: React.ReactNode; icon?: LucideIcon }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/10 px-4 py-2 text-sm font-semibold no-underline transition-colors hover:bg-primary/20"
    >
      {Icon && <Icon className="h-3.5 w-3.5 text-primary" />}
      <span className="text-foreground">{children}</span>
    </Link>
  );
}

/** Card chrome: a clickable header face with an accordion body underneath.
 *
 * Renders nothing when this card's module is switched off for the tenant. The
 * check lives HERE rather than at each call site so a card added later cannot
 * forget it — the only thing a new card has to do is appear in CARD_MODULE, and
 * one that is always available simply stays out of that map.
 */
function ModuleCard({
  id,
  open,
  onToggle,
  kicker,
  aside,
  icon: Icon,
  tone = "gold",
  title,
  children,
  below,
  action,
  detail,
}: {
  id: string;
  open: boolean;
  onToggle: (id: string) => void;
  kicker: string;
  aside?: React.ReactNode;
  icon: LucideIcon;
  tone?: IconTone;
  title: React.ReactNode;
  children?: React.ReactNode;
  /** Full-width content under the icon row (message threads). */
  below?: React.ReactNode;
  /** Always-visible action, bottom-right. Sits OUTSIDE the toggle <button> —
   *  an anchor nested in a button is invalid HTML. */
  action?: React.ReactNode;
  detail?: React.ReactNode;
}) {
  const isModuleVisible = useModuleVisibility();
  if (!isModuleVisible(CARD_MODULE[id])) return null;

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col rounded-2xl border bg-card p-5 text-left shadow-card transition-all duration-200",
        open ? "border-primary/40 shadow-elegant" : "border-border hover:border-primary/30",
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        className="flex w-full min-w-0 cursor-pointer flex-col text-left"
      >
        {/* The chevron is the only thing that says these cards open at all:
            before it, the whole face was a silent <button> and the detail rows
            below (every "open the module" link on the page) went unfound. */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <Kicker>{kicker}</Kicker>
          <span className="flex shrink-0 items-center gap-2">
            {aside}
            <ChevronRight
              aria-hidden
              className={cn(
                "h-4 w-4 shrink-0 transition-transform duration-200",
                open ? "rotate-90 text-primary" : "text-muted-foreground",
              )}
            />
          </span>
        </div>
        <div className="flex min-w-0 items-start gap-4">
          <span className={cn("grid h-12 w-12 shrink-0 place-items-center rounded-xl", ICON_TONE[tone])}>
            <Icon className="h-[22px] w-[22px]" strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="heading-medium m-0 mb-1 font-display text-[1.15rem] leading-snug text-foreground">{title}</h3>
            {children}
          </div>
        </div>
        {below}
      </button>
      {action ? <div className="mt-auto flex justify-end pt-3">{action}</div> : null}
      {open && detail ? (
        <div className="mt-4 border-t border-border pt-3">{detail}</div>
      ) : null}
    </div>
  );
}

/** Gold-on-dark primary action. Kept as a Link so every card leads somewhere. */
function CardLink({ to, children, tone = "primary" }: { to: string; children: React.ReactNode; tone?: "primary" | "ghost" }) {
  return (
    <Link
      to={to}
      className={cn(
        // text-primary-foreground lands here only because index.css carries an
        // `a.bg-gold-gradient { color: … }` rule — the unlayered `a { color:
        // inherit }` reset beats Tailwind's layered text-* utilities, so any
        // OTHER colour on an anchor has to sit on a child element instead.
        "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
        tone === "primary"
          ? "bg-gold-gradient text-primary-foreground hover:brightness-[1.06]"
          : "border border-border hover:border-primary/40 hover:bg-accent/60",
      )}
    >
      {children}
    </Link>
  );
}

/** A listing row inside an expanded panel.
 *
 * Only ever used in the accordion BODY, never in the card face: the face is a
 * <button> that toggles the card, and an anchor nested inside a button is
 * invalid HTML that browsers resolve unpredictably.
 *
 * `/listings/:id` is a shared route and is deliberately NOT org-prefixed — see
 * the canonicalisation note in App.tsx.
 */
function DealRow({ to, name, right }: { to: string; name: string; right?: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="group flex items-baseline gap-3 border-b border-border py-2 text-sm last:border-b-0 hover:bg-accent/40"
    >
      <span className="min-w-0 flex-1 truncate text-foreground group-hover:text-primary">{name}</span>
      {right}
    </Link>
  );
}

/**
 * A label/value line inside an expanded card.
 *
 * The value column is the one that flexes, NOT the label: these values are
 * user data of unbounded length — "Sector" is every category the buyer picked,
 * joined with commas. A `shrink-0` value (what this was) refuses to give up any
 * width, so a long one overflowed leftwards straight through its own label and
 * rendered "SeHVAC, Landscape, Electric, …" on top of "Sector". Pinning the
 * label instead and letting the value wrap inside `min-w-0` keeps the two
 * columns apart at any length; call sites that want a hard stop add their own
 * `line-clamp-*` (the buybox does).
 *
 * Sans, not mono: the values here are mostly prose — sector lists, place names,
 * partner names. `tabular-nums` still lines up the figures among them.
 */
function Row({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-x-4 text-sm">
      <span className="shrink-0 text-muted-foreground">{left}</span>
      <span className="min-w-0 flex-1 text-right tabular-nums text-foreground">{right}</span>
    </div>
  );
}

/**
 * What a member-only card shows a Basic buyer.
 *
 * Deliberately says what the feature DOES rather than only that it is locked:
 * the dashboard stays open to Basic precisely so it can make the case for
 * upgrading, and "Members only" on its own makes no case at all.
 */
function LockedNote({ children, to }: { children: React.ReactNode; to: string }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <span>{children}</span>
      </p>
      <CardLink to={to}>Upgrade to unlock</CardLink>
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return <p className="m-0 text-sm leading-relaxed text-muted-foreground">{children}</p>;
}

const DOC_DOT: Record<DocItem["state"], string> = {
  done: "bg-emerald-500 border-emerald-500",
  open: "bg-amber-500 border-amber-500",
  todo: "bg-transparent border-muted-foreground/50",
};

/** Status pill beside each document — semantic green / amber, as the dots. */
const DOC_PILL: Record<DocItem["state"], string> = {
  done: "border-emerald-200 bg-emerald-50 text-emerald-700",
  open: "border-amber-200 bg-amber-50 text-amber-700",
  todo: "border-border bg-muted text-muted-foreground",
};

/** "Manish R" -> "MR" for the message-thread avatars. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

const RISK_DOT: Record<Risk["level"], string> = {
  ok: "bg-success",
  warn: "bg-warning",
  info: "bg-muted-foreground",
};

/**
 * Areas whose routes are declared `/:orgSlug?/…` in App.tsx and therefore exist
 * in both bare and org-prefixed form. Mirrors ORG_PREFIXABLE_AREAS there, plus
 * /academy, which carries the optional segment too.
 *
 * This matters because the payload mixes both kinds of destination: the
 * documents card links to /buyer/settings/documents (prefixable) AND to
 * /sign-nda, which is a bare shared route. Prefixing that one would send an
 * ECFA buyer to /ecfa/sign-nda — a path with no route behind it, which lands
 * them back on "/" instead of the signature they were sent to collect.
 */
const ORG_PREFIXABLE = ["/buyer/", "/seller/", "/bl-advisor/", "/advisor/", "/admin/", "/resources/", "/academy"];

/**
 * Module-switchboard key per card, matching MODULES in
 * app/services/module_visibility.py. A tenant's super admin can switch any of
 * these off, at which point the sidebar stops rendering the entry — so the
 * dashboard has to stop summarising it too, or it becomes the one surface still
 * advertising a page that is no longer reachable.
 *
 * Cards absent from this map are always shown: the buybox lives in settings,
 * documents in settings, and meetings has no module of its own yet.
 */
const CARD_MODULE: Record<string, string> = {
  matches: "ai_deal_matching",
  crm: "deal_crm",
  shortlist: "favorites",
  messages: "interests",
  financing: "funding_marketplace",
  academy: "bl_academy",
};

function scopedPath(to: string, orgSlug: string | null | undefined): string {
  if (!orgSlug) return to;
  return ORG_PREFIXABLE.some((area) => to === area || to.startsWith(area))
    ? orgScopedPath(to, orgSlug)
    : to;
}

/**
 * The Basic plan's standing prompt.
 *
 * A Basic buyer keeps the dashboard — they have two lifetime full-detail
 * unlocks, and the journey, buybox, documents and Academy cards below are all
 * genuinely theirs. This band is what the rest of the page is for: it shows the
 * allowance they still have, then asks for the upgrade.
 */
function UpgradeBanner({ access, to }: { access: Access; to: string }) {
  const total = access.free_unlocks_total ?? 0;
  const left = access.free_unlocks_remaining ?? 0;
  const spent = left <= 0;

  return (
    <section className="public-hero-shell mb-8 rounded-2xl border border-primary/25 p-5 text-foreground">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0 flex-[1_1_22rem]">
          <div className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-primary">
            Basic plan
          </div>
          <h2 className="heading-bold m-0 mb-1.5 font-display text-[1.45rem]">
            {spent
              ? "You've used your free deal unlocks for now"
              : `${left} of ${total} free deal unlock${total === 1 ? "" : "s"} left this month`}
          </h2>
          <p className="m-0 text-sm leading-relaxed text-muted-foreground">
            {spent
              ? "2 more arrive every week. Upgrade to open every listing in full, plus your shortlist, AI deal matching and the Deal CRM."
              : "Basic earns two full-detail listings a week, each with BL Advisor chat; unused ones carry over until the month ends. Upgrading opens every deal, plus your shortlist, AI deal matching and the Deal CRM."}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          {total > 0 && (
            <div className="flex items-center gap-1.5" aria-hidden>
              {Array.from({ length: total }, (_, i) => (
                <span
                  key={i}
                  className={cn(
                    "h-2.5 w-8 rounded-full",
                    i < left ? "bg-primary" : "bg-muted-foreground/30",
                  )}
                />
              ))}
            </div>
          )}
          <CardLink to={to}>See plans</CardLink>
        </div>
      </div>
    </section>
  );
}

// ── page ─────────────────────────────────────────────────────────────

export default function BuyerDashboard() {
  const { user } = useAuth();
  const orgSlug = user?.org_slug;
  const path = useCallback((p: string) => scopedPath(p, orgSlug), [orgSlug]);

  const [data, setData] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [allActions, setAllActions] = useState(false);

  // "loading" until the scoring call lands; "locked" when it comes back 402,
  // which is a genuinely different card from an empty result — /deal-scoring
  // sits behind require_active_user, so a free/Basic buyer is not being told
  // nothing fits, they are being told to subscribe. "error" is any OTHER
  // failure (5xx, timeout, network): it must not fall through to `[]`, whose
  // empty state tells the buyer to widen their buybox — advice about their
  // criteria for what was our outage. Scoring runs ~18s, so timeouts happen.
  const [matchRows, setMatchRows] = useState<MatchRowsState>("loading");
  // Bumped by the card's "Try again" to re-run the scoring fetch below.
  const [matchAttempt, setMatchAttempt] = useState(0);
  const retryMatches = useCallback(() => {
    setMatchRows("loading");
    setMatchAttempt((n) => n + 1);
  }, []);
  // Same switchboard the card itself reads — checked here too so the scoring
  // request is never made for a card that will not render.
  const isModuleVisible = useModuleVisibility();
  const matchesVisible = isModuleVisible(CARD_MODULE.matches);
  const shortlistVisible = isModuleVisible(CARD_MODULE.shortlist);
  const financingVisible = isModuleVisible(CARD_MODULE.financing);

  const toggle = useCallback((id: string) => setOpen((cur) => (cur === id ? null : id)), []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<DashboardPayload>("/dashboard/buyer")
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : "Could not load your dashboard.");
      });
    return () => { cancelled = true; };
  }, []);

  // The slow half, off the critical path. Two reasons it is guarded rather
  // than simply fired:
  //
  //  * Scoring is the expensive call on this page (it ranks every active
  //    listing), so it must not run for a tenant whose AI Deal Matching module
  //    is switched off — the card it feeds is not even rendered.
  //  * A failure here is not a page failure. /business-listings/deal-scoring
  //    keeps its own member gate and 402s a free buyer, which has to leave the
  //    rest of the dashboard standing and show this one card as locked.
  useEffect(() => {
    // `!data` matters as much as the locked check: on the first render the
    // payload has not arrived, so `data?.matches.locked` is undefined and a
    // Basic buyer would fire the expensive scoring call before we know it is
    // locked shut — only to have it come back 402. Wait for the answer.
    if (!matchesVisible || !data || data.matches.locked) return;
    let cancelled = false;
    api
      .get<{ deals: ScoredDeal[] }>("/business-listings/deal-scoring?limit=3")
      .then((res) => {
        if (cancelled) return;
        setMatchRows(
          (res.deals ?? []).map((d) => ({
            listing_id: d.listing_id,
            reference_code: d.reference_code,
            score: Math.round(d.fit_score),
            name: d.title,
            meta:
              [
                d.location,
                d.revenue ? `${money(d.revenue)} rev` : null,
                money(d.asking_price),
                d.category,
              ]
                .filter(Boolean)
                .join(" · ") || "Open the listing for details",
            image_url: d.listing_image_url ?? null,
            location: d.location,
            revenue: money(d.revenue),
            asking: money(d.asking_price),
            category: d.category,
            asking_price: d.asking_price,
          })),
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setMatchRows(e instanceof ApiError && e.status === 402 ? "locked" : "error");
      });
    return () => { cancelled = true; };
    // `data` is set exactly once, so depending on the whole object costs one
    // extra no-op run (the early return above) rather than a repeated fetch.
    // `matchAttempt` only changes when the buyer asks for a retry.
  }, [matchesVisible, data, matchAttempt]);

  if (error) {
    return (
      <main className="mx-auto w-full max-w-[1440px] px-4 py-16 sm:px-6">
        <h1 className="font-display text-[1.875rem]">Dashboard unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        <Link to={path("/buyer/home")} className="mt-6 inline-flex text-sm hover:underline">
          <span className="text-primary">Browse listings instead →</span>
        </Link>
      </main>
    );
  }

  if (!data) return <Loader fullScreen label="Building your dashboard" />;

  const { access, buyer, position, journey, buybox, matches, pipeline, shortlist, messages, financing, documents, meetings, academy, forecast, actions } = data;
  // Never a bare "/choose-plan": ChoosePlanRoute bounces anyone who already
  // subscribes back to /buyer/home, so an Explorer buyer's "upgrade" would go
  // nowhere. upgradeCtaPath sends subscribers to Change Plan instead.
  //  * plans         — member-only cards (only ever locked for Basic)
  //  * operatorPlans — the Deal CRM, forecast and Niva, locked below Operator
  const plans = path(upgradeCtaPath(user));
  const operatorPlans = path(upgradeCtaPath(user, "Operator"));
  const journeyPct = Math.round(
    (journey.steps.filter((st) => st.status === "complete").length / Math.max(journey.steps.length, 1)) * 100,
  );

  const dateLabel = new Date(`${buyer.today_iso}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    // One layout for every width: phones get the same dashboard as the web,
    // its grids simply collapse to fewer columns.
    <main className="mx-auto w-full max-w-[1440px] px-4 pb-16 pt-6 sm:px-6 lg:pt-8">
      {/* ── hero: greeting + search ─────────────────────────────── */}
      <WelcomeHero
        firstName={buyer.first_name}
        planName={access.plan_name}
        kicker={<>{dateLabel} · Week {buyer.week_number} of your search</>}
        path={path}
        className="mb-6 rounded-2xl border lg:mb-8"
        showSearch={false}
      >
        {/* Live position, on the hero's dark ground. */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <HeroStat icon={FileText} value={position.deals_in_pipeline} label="Deals in pipeline" />
          <HeroStat icon={Briefcase} value={position.live_deals} label="In LOI or diligence" />
          <HeroStat icon={ChartColumn} value={position.ev_live_label} label="Value under diligence" />
          <HeroStat icon={Mail} value={position.interests_raised} label="Approaches raised" />
        </div>
      </WelcomeHero>

      {access.is_basic && <UpgradeBanner access={access} to={plans} />}

      {/* ── acquisition journey rail ─────────────────────────────── */}
      <section className="mb-10 overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-end justify-between gap-4 px-5 pb-3 pt-4">
          <div>
            <h2 className="heading-eyebrow m-0 mb-1 text-sm uppercase">Your acquisition journey</h2>
            <p className="m-0 text-sm text-muted-foreground">
              Stage {journey.current_step} of {journey.steps.length} ·{" "}
              <span className="font-semibold text-primary">{journey.current_label}</span>
            </p>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-sans text-2xl font-bold leading-none tabular-nums text-foreground">{journeyPct}%</span>
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">complete</span>
          </div>
        </div>
        {/* Overall progress, edge to edge under the header. */}
        <div className="mx-5 mb-4 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-gradient-to-r from-success to-primary transition-all duration-700"
            style={{ width: `${journeyPct}%` }}
          />
        </div>
        <ol className="m-0 grid list-none grid-cols-1 gap-3 px-5 pb-4 sm:grid-cols-2 lg:grid-cols-5">
          {journey.steps.map((s, i) => {
            const done = s.status === "complete";
            const now = s.status === "current";
            const last = i === journey.steps.length - 1;
            const Icon = JOURNEY_ICONS[i] ?? Flag;
            const body = (
              <div
                className={cn(
                  "relative flex h-full flex-col rounded-xl border p-3 transition-all duration-200",
                  done && "border-success/30 bg-success/[0.06]",
                  now && "border-primary/60 bg-primary/10 shadow-glow",
                  !done && !now && "border-border bg-background/40",
                  "cursor-pointer hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-card",
                )}
              >
                {/* Connector across the gap to the next tile. */}
                {!last && (
                  <span
                    aria-hidden
                    className={cn("absolute -right-3 top-[1.75rem] hidden h-0.5 w-3 lg:block", done ? "bg-success" : "bg-border")}
                  />
                )}
                <div className="mb-2 flex items-center gap-2.5">
                  <span
                    className={cn(
                      "relative grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                      done ? "bg-success/15" : now ? "bg-gold-gradient shadow-glow" : "bg-muted",
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-4 w-4",
                        done ? "text-success" : now ? "text-primary-foreground" : "text-muted-foreground",
                      )}
                    />
                    {done && (
                      <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-success ring-2 ring-card">
                        <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                  </span>
                  <div className="min-w-0">
                    {/* Step number doubles as the status line (replaces the old pill row). */}
                    <div
                      className={cn(
                        "flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em]",
                        done ? "text-success" : now ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {now && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />}
                      Step {String(i + 1).padStart(2, "0")} · {done ? "Complete" : now ? "You are here" : "Upcoming"}
                    </div>
                    <div className={cn("truncate text-sm font-bold leading-snug", done || now ? "text-foreground" : "text-muted-foreground")}>
                      {s.title}
                    </div>
                  </div>
                </div>
                <div className="line-clamp-2 text-xs leading-snug text-muted-foreground">{s.note}</div>
              </div>
            );
            const target = journeyStepTarget(s.key, data, plans, operatorPlans);
            return (
              <li key={s.key}>
                <Link
                  to={target.scoped ? target.to : path(target.to)}
                  aria-label={`${s.title}: ${s.note}`}
                  className="block h-full no-underline"
                >
                  {body}
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      {/* ── modules + rail ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,2.05fr)_minmax(20rem,1fr)]">
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">

          {/* Matched to your buybox + Your buybox ------------------ */}
          <div
            className={cn(
              "grid min-w-0 grid-cols-1 items-stretch gap-4 sm:col-span-2",
              (matchesVisible || shortlistVisible) && "lg:grid-cols-[minmax(0,1.65fr)_minmax(15rem,1fr)]",
            )}
          >
            {(matchesVisible || shortlistVisible) && (
              <MatchedCard
                matches={matches}
                matchRows={matchRows}
                onRetry={retryMatches}
                shortlist={shortlist}
                budgetMax={buybox.budget_max ?? null}
                showMatches={matchesVisible}
                showShortlist={shortlistVisible}
                path={path}
                plans={plans}
              />
            )}
            <BuyboxSummary
              buybox={buybox}
              to={path("/buyer/settings/profile")}
              nextStep={
                !financing.locked && !financing.prequalified && financingVisible
                  ? {
                      title: "Know your buying power.",
                      body: "Lender pre-qualification is still open. Explore financing for your target budget.",
                      cta: "Explore financing",
                      to: path("/buyer/funding"),
                    }
                  : actions[0]
                    ? { title: "Keep your search moving.", body: actions[0].text, cta: "Take the next step", to: path(actions[0].to) }
                    : null
              }
            />
          </div>

          {/* Deal CRM ---------------------------------------------- */}
          <ModuleCard
            id="crm"
            open={open === "crm"}
            onToggle={toggle}
            kicker="Deal CRM"
            icon={Folder}
            title="Portfolio pipeline"
            action={
              pipeline.locked ? (
                <SoftAction to={operatorPlans} icon={Lock}>Upgrade to unlock</SoftAction>
              ) : pipeline.total === 0 ? (
                <SoftAction to={path("/buyer/home")} icon={Plus}>Add a listing</SoftAction>
              ) : (
                <SoftAction to={path("/buyer/pipeline")}>Open deal CRM</SoftAction>
              )
            }
            aside={
              <span className="text-xs text-muted-foreground">
                {pipeline.locked ? "Operator" : `${pipeline.total} tracked`}
              </span>
            }
            detail={
              pipeline.locked ? (
                <LockedNote to={operatorPlans}>
                  The Deal CRM board tracks each deal through watching, contacted, LOI, diligence,
                  financing and closing. It comes with the Operator plan.
                </LockedNote>
              ) : (
              <div className="flex flex-col">
                {pipeline.deals.map((d) => (
                  <DealRow
                    key={d.listing_id}
                    to={`/listings/${d.listing_id}`}
                    name={d.name}
                    right={
                      <>
                        <span className="shrink-0 font-mono tabular-nums text-muted-foreground">{d.ev}</span>
                        <span className="w-24 shrink-0 text-right font-mono text-[11px] uppercase tracking-[0.08em] text-primary">
                          {d.stage_label}
                        </span>
                      </>
                    }
                  />
                ))}
                <div className="mt-3">
                  <CardLink to={path("/buyer/pipeline")}>Open deal CRM</CardLink>
                </div>
              </div>
              )
            }
          >
            {pipeline.locked ? (
              <EmptyNote>Track every deal from first look to close on your own pipeline board.</EmptyNote>
            ) : pipeline.total === 0 ? (
              <EmptyNote>Nothing tracked yet. Add a listing to the Deal CRM to watch it move through the stages.</EmptyNote>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {pipeline.groups.map((g) => (
                  <div key={g.key} className={cn("border-t-2 pt-2", g.is_late && g.count > 0 ? "border-primary" : "border-border")}>
                    <div className="font-display text-[1.6rem] leading-none tabular-nums">{g.count}</div>
                    <div className="text-xs leading-snug text-muted-foreground">{g.label}</div>
                  </div>
                ))}
              </div>
            )}
          </ModuleCard>

          {/* Shortlist --------------------------------------------- */}
          <ModuleCard
            id="shortlist"
            open={open === "shortlist"}
            onToggle={toggle}
            kicker="Shortlist"
            icon={Heart}
            tone="rose"
            title={<>Favourites &amp; watchlist</>}
            action={
              shortlist.locked ? (
                <SoftAction to={plans} icon={Lock}>Upgrade to unlock</SoftAction>
              ) : (
                <SoftAction to={path("/buyer/home")}>Browse listings</SoftAction>
              )
            }
            aside={
              <span className="text-xs text-muted-foreground">
                {shortlist.saved} saved · {position.listings_viewed} viewed
              </span>
            }
            detail={
              shortlist.locked ? (
                <LockedNote to={plans}>Saving and comparing listings comes with any paid plan.</LockedNote>
              ) : (
              <div>
                {shortlist.items.length > 0 && (
                  <div className="mb-3 flex flex-col">
                    {shortlist.items.map((i) => (
                      <DealRow
                        key={i.listing_id}
                        to={`/listings/${i.listing_id}`}
                        name={i.name}
                        right={<span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{i.note}</span>}
                      />
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <CardLink to={path("/buyer/favorites")}>Open shortlist</CardLink>
                  <CardLink to={path("/buyer/home")} tone="ghost">Find more</CardLink>
                </div>
              </div>
              )
            }
          >
            {shortlist.locked ? (
              <EmptyNote>Keep the deals worth a second look in one place, and compare them side by side.</EmptyNote>
            ) : shortlist.items.length === 0 ? (
              <EmptyNote>Nothing saved yet — tap the heart on any listing to keep it here.</EmptyNote>
            ) : (
              <div className="flex flex-col">
                {shortlist.items.map((s) => (
                  <div key={s.listing_id} className="flex items-baseline gap-3 border-t border-border py-2.5 text-sm">
                    <span className="min-w-0 flex-1 truncate">{s.name}</span>
                    <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{s.note}</span>
                  </div>
                ))}
              </div>
            )}
          </ModuleCard>

          {/* Messages ---------------------------------------------- */}
          <ModuleCard
            id="messages"
            open={open === "messages"}
            onToggle={toggle}
            kicker="Messages"
            icon={MessageSquare}
            tone="sky"
            title="BL Advisor threads"
            below={
              !messages.locked && messages.items.length > 0 ? (
                <div className="mt-4 flex flex-col">
                  {messages.items.map((m) => (
                    <div key={m.interest_id} className="flex items-center gap-3 border-t border-border py-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-50 text-xs font-semibold text-sky-700">
                        {initials(m.who)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex justify-between gap-3 text-sm">
                          <span className="min-w-0 truncate">
                            <span className="font-semibold text-foreground">{m.who}</span>
                            {m.listing_title ? <span className="text-muted-foreground"> · {m.listing_title}</span> : null}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {m.unread > 0 ? <span className="mr-1.5 text-primary">●</span> : null}
                            {m.when}
                          </span>
                        </span>
                        {/* Sans has no italic face loaded — see the hero note. */}
                        <span className="mt-0.5 block line-clamp-1 text-xs text-muted-foreground">{m.line}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : null
            }
            aside={
              <span className="text-xs text-muted-foreground">
                {messages.awaiting > 0 ? `${messages.awaiting} awaiting you` : `${messages.total} thread${messages.total === 1 ? "" : "s"}`}
              </span>
            }
            detail={
              messages.locked ? (
                <LockedNote to={plans}>
                  Raising interest and messaging an advisor about a deal comes with any paid plan.
                </LockedNote>
              ) : (
              <div>
                {messages.items.length > 0 && (
                  <div className="mb-3 flex flex-col">
                    {messages.items.map((m) => (
                      <DealRow
                        key={m.interest_id}
                        to={path(`/buyer/interests?tab=chats&thread=${m.interest_id}`)}
                        name={m.listing_title ?? m.who}
                        right={<span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{m.when}</span>}
                      />
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <CardLink to={path("/buyer/interests")}>Open inbox</CardLink>
                  <CardLink to={path("/buyer/support")} tone="ghost">Contact support</CardLink>
                </div>
              </div>
              )
            }
          >
            {messages.locked ? (
              <EmptyNote>Talk directly to the BL Advisor handling a deal, in a thread per listing.</EmptyNote>
            ) : messages.items.length === 0 ? (
              <EmptyNote>No conversations yet. Raise interest on a listing and an advisor picks it up from there.</EmptyNote>
            ) : null}
          </ModuleCard>

          {/* Financing --------------------------------------------- */}
          <ModuleCard
            id="financing"
            open={open === "financing"}
            onToggle={toggle}
            kicker="Financing"
            icon={CircleDollarSign}
            title={financing.ceiling_label ? `Targeting up to ${financing.ceiling_label}` : "Acquisition financing"}
            action={
              financing.locked ? (
                <SoftAction to={plans} icon={Lock}>Upgrade to unlock</SoftAction>
              ) : (
                <SoftAction to={path("/buyer/funding")}>Explore financing</SoftAction>
              )
            }
            aside={
              <span className="text-xs text-muted-foreground">
                {financing.deals_needing_funding > 0
                  ? `${financing.deals_needing_funding} deal${financing.deals_needing_funding === 1 ? "" : "s"} need funding`
                  : financing.application_count > 0
                    ? `${financing.application_count} application${financing.application_count === 1 ? "" : "s"}`
                    : null}
              </span>
            }
            detail={
              financing.locked ? (
                <LockedNote to={plans}>
                  Debt placement pre-qualification with our funding partners comes with any paid plan.
                </LockedNote>
              ) : (
              <div className="flex flex-col gap-2">
                {financing.applications.length > 0 ? (
                  financing.applications.map((a) => (
                    <Row key={a.id} left={a.partner_name} right={`${a.reference_code} · ${a.status_label}`} />
                  ))
                ) : (
                  <EmptyNote>No application on file. Pre-qualification takes a few minutes and firms up your offer.</EmptyNote>
                )}
                <div className="mt-2">
                  <CardLink to={path("/buyer/funding")}>Open financing</CardLink>
                </div>
              </div>
              )
            }
          >
            <p className="m-0 mb-3 text-sm text-muted-foreground">
              {financing.locked
                ? "Get pre-qualified with our lending partners before you make an offer."
                : financing.prequalified
                ? financing.prequalifications.join(" · ")
                  : "Not yet pre-qualified — lenders move faster when you are."}
            </p>
            {!financing.locked && (
            <div className="flex items-stretch gap-6">
              <Stat value={`${financing.equity_injection_pct}%`} label="typical equity injection" />
              <span aria-hidden className="w-px bg-border" />
              <Stat
                value={financing.cash_required_label ?? "—"}
                label="cash at your ceiling"
              />
            </div>
            )}
          </ModuleCard>

          {/* Documents --------------------------------------------- */}
          <ModuleCard
            id="docs"
            open={open === "docs"}
            onToggle={toggle}
            kicker="Documentation"
            icon={FileText}
            tone="orange"
            title={<>Agreements &amp; paperwork</>}
            aside={
              <span className="text-xs text-muted-foreground">
                {documents.needs_signature > 0 ? `${documents.needs_signature} needs you` : "All clear"}
              </span>
            }
            detail={
              <div>
                <div className="mb-3 flex flex-col">
                  {documents.items.map((d) => (
                    <Link
                      key={d.name}
                      to={path(d.to)}
                      className="group flex items-baseline gap-3 border-b border-border py-2 text-sm last:border-b-0 hover:bg-accent/40"
                    >
                      <span className="min-w-0 flex-1 truncate text-foreground group-hover:text-primary">{d.name}</span>
                      <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                        {d.detail}
                      </span>
                    </Link>
                  ))}
                </div>
                <CardLink to={path("/buyer/settings/documents")}>Open documents</CardLink>
              </div>
            }
          >
            <div className="mt-2 flex flex-col">
              {documents.items.map((d) => (
                <div key={d.name} className="flex items-center gap-3 py-1.5 text-sm">
                  <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full border", DOC_DOT[d.state])} />
                  <span className="min-w-0 flex-1 truncate text-foreground">{d.name}</span>
                  <span
                    className={cn(
                      "shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em]",
                      DOC_PILL[d.state],
                    )}
                  >
                    {d.detail}
                  </span>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Meetings ---------------------------------------------- */}
          <ModuleCard
            id="meetings"
            open={open === "meetings"}
            onToggle={toggle}
            kicker="Meetings"
            icon={CalendarDays}
            tone="blue"
            title={<>Next with seller &amp; advisor</>}
            action={<SoftAction to={path("/buyer/interests")}>Schedule a call</SoftAction>}
            aside={
              <span className="text-xs text-muted-foreground">
                {meetings.items.length > 0 ? `${meetings.items.length} upcoming` : "Nothing booked"}
              </span>
            }
            detail={
              <div className="flex flex-wrap gap-2">
                <CardLink to={path("/buyer/interests")}>Message your advisor</CardLink>
              </div>
            }
          >
            {meetings.items.length === 0 ? (
              <EmptyNote>
                Scheduling isn't wired up yet — arrange calls through your advisor thread and they'll appear here
                once the module ships.
              </EmptyNote>
            ) : (
              <div className="flex flex-col">
                {meetings.items.map((m) => (
                  <div key={`${m.when}-${m.title}`} className="flex gap-3 border-t border-border py-2.5">
                    <span className="w-16 shrink-0 font-mono text-xs tabular-nums text-primary">{m.when}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{m.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">{m.with}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ModuleCard>

          {/* Academy ----------------------------------------------- */}
          <ModuleCard
            id="academy"
            open={open === "academy"}
            onToggle={toggle}
            kicker="BL Academy"
            icon={GraduationCap}
            tone="violet"
            title="Certification track"
            aside={
              <span className="text-xs text-muted-foreground">
                {academy.locked
                  ? "Members"
                  : academy.lessons_total > 0
                    ? `${academy.lessons_completed}/${academy.lessons_total} lessons`
                    : "No lessons yet"}
              </span>
            }
            detail={
              academy.locked ? (
                <LockedNote to={plans}>BL Academy comes with any paid plan.</LockedNote>
              ) : (
              <div>
                <p className="m-0 mb-3 text-sm leading-relaxed text-muted-foreground">
                  {academy.resume
                    ? <>Continue <span className="font-semibold text-foreground">{academy.resume.name}</span> — {academy.resume.total - academy.resume.done} lesson{academy.resume.total - academy.resume.done === 1 ? "" : "s"} left on that stage.</>
                    : "Every published lesson is complete. New ones appear as the curriculum grows."}
                </p>
                <CardLink to={path("/academy")}>
                  {academy.resume ? "Resume course" : "Open the Academy"}
                </CardLink>
              </div>
              )
            }
          >
            {academy.locked ? (
              <EmptyNote>Video lessons across the whole acquisition path, with a buyer toolkit and case studies.</EmptyNote>
            ) : academy.courses.length === 0 ? (
              <EmptyNote>No lessons published to buyers yet — the curriculum is on its way.</EmptyNote>
            ) : (
              <div className="flex flex-col gap-2.5">
                {academy.courses.map((c) => (
                  <div key={c.key}>
                    <div className="mb-1 flex justify-between text-sm">
                      <span className="min-w-0 truncate">{c.name}</span>
                      <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{c.pct}%</span>
                    </div>
                    <div className="h-[3px] bg-border">
                      <div className="h-[3px] bg-primary transition-all" style={{ width: `${c.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ModuleCard>
        </div>

        {/* ── sticky rail ────────────────────────────────────────── */}
        <aside className="flex min-w-0 flex-col gap-4 xl:sticky xl:top-20">
          <ForecastPanel
            forecast={forecast}
            open={open === "forecast"}
            onToggle={toggle}
            to={path("/buyer/pipeline")}
            upgradeTo={operatorPlans}
          />
          <NivaPanel
            entitled={canUseNiva(user)}
            upgradeTo={operatorPlans}
            buyboxTo={path("/buyer/settings/profile?edit=buybox")}
          />

          <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
            <div className="mb-2 flex items-center justify-between gap-3">
              <Kicker>Next best actions</Kicker>
              {/* "See all" only when there is more than the first three to see —
                  a toggle that reveals nothing would be a dead control. */}
              {actions.length > 3 && (
                <button
                  type="button"
                  onClick={() => setAllActions((v) => !v)}
                  className="inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-foreground hover:text-primary"
                >
                  {allActions ? "Show less" : "See all"}
                  <ArrowDownRight className={cn("h-3.5 w-3.5 text-primary transition-transform", allActions && "rotate-180")} />
                </button>
              )}
            </div>
            <div className="flex flex-col">
              {(allActions ? actions : actions.slice(0, 3)).map((a, i) => (
                <Link
                  key={a.no}
                  to={path(a.to)}
                  className={cn(
                    "group flex items-center gap-3 py-3 no-underline",
                    i > 0 && "border-t border-border",
                  )}
                >
                  <span className="grid h-8 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-xs font-semibold tabular-nums text-foreground">
                    {a.no}
                  </span>
                  <span className="min-w-0 flex-1 text-sm leading-snug text-foreground group-hover:text-primary">
                    {a.text}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" />
                </Link>
              ))}
            </div>
          </div>

          <QuickLinks
            path={path}
            isBasic={access.is_basic}
            plans={plans}
            crmLocked={!!pipeline.locked}
            operatorPlans={operatorPlans}
          />
        </aside>
      </div>
    </main>
  );
}

// ── matched + buybox cards ───────────────────────────────────────────

/** Gold "label + arrow" text link. Colour sits on the children: the unlayered
 *  `a { color: inherit }` reset beats Tailwind's text-* on the anchor itself. */
function GoldTextLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold no-underline">
      <span className="text-primary group-hover:underline">{children}</span>
      <ArrowUpRight className="h-4 w-4 text-primary" />
    </Link>
  );
}

/** Listing photo, or a neutral tile when the listing has none or it fails. */
function ListingPhoto({ src }: { src?: string | null }) {
  const [failed, setFailed] = useState(false);
  const cls = "h-[6.5rem] w-[4.75rem] shrink-0 rounded-lg";
  if (!src || failed) {
    return (
      <div className={cn(cls, "grid place-items-center bg-muted")}>
        <Building2 className="h-6 w-6 text-muted-foreground/60" />
      </div>
    );
  }
  return <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className={cn(cls, "object-cover")} />;
}

interface OpportunityRow {
  listing_id: string;
  name: string;
  category?: string | null;
  location?: string | null;
  image_url?: string | null;
  asking?: string | null;
  revenue?: string | null;
  asking_price?: number | null;
}

/** One listing in the matched card: photo, figures, heart, budget tag, link. */
function OpportunityItem({
  row,
  budgetMax,
  saved,
  busy,
  onHeart,
}: {
  row: OpportunityRow;
  budgetMax: number | null;
  saved: boolean;
  busy: boolean;
  onHeart: () => void;
}) {
  const to = `/listings/${row.listing_id}`;
  const inBudget =
    budgetMax != null && row.asking_price != null && row.asking_price > 0 && row.asking_price <= budgetMax;
  return (
    <li className="border-b border-border py-4 first:pt-1 last:border-b-0">
      <div className="flex gap-3">
        <Link to={to} className="shrink-0" tabIndex={-1} aria-hidden>
          <ListingPhoto src={row.image_url} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {row.category && (
                <div className="truncate text-xs font-semibold uppercase tracking-[0.08em] text-primary">{row.category}</div>
              )}
              <Link to={to} className="no-underline">
                <span className="line-clamp-2 text-base font-semibold leading-snug text-foreground hover:text-primary">
                  {row.name}
                </span>
              </Link>
            </div>
            <button
              type="button"
              onClick={onHeart}
              disabled={busy}
              aria-pressed={saved}
              aria-label={saved ? "Remove from shortlist" : "Save to shortlist"}
              className="-mr-1 grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-full hover:bg-accent disabled:opacity-60"
            >
              <Heart className={cn("h-[1.1rem] w-[1.1rem]", saved ? "fill-primary text-primary" : "text-muted-foreground")} />
            </button>
          </div>
          {row.location && (
            <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{row.location}</span>
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
            <div>
              <div className="text-xs text-muted-foreground">Asking price</div>
              <div className="text-base font-bold tabular-nums text-foreground">{row.asking ?? "On request"}</div>
            </div>
            {row.revenue && (
              <div>
                <div className="text-xs text-muted-foreground">Annual revenue</div>
                <div className="text-base font-bold tabular-nums text-foreground">{row.revenue}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        {inBudget ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
            <Check className="h-3.5 w-3.5" />
            Within your budget
          </span>
        ) : (
          <span />
        )}
        <GoldTextLink to={to}>View opportunity</GoldTextLink>
      </div>
    </li>
  );
}

/**
 * "Matched to your Buybox": the top scored listings and the buyer's shortlist
 * as two tabs of photo rows. Hearts write straight to /favorites, and the
 * shortlist tab updates in place so the two tabs never disagree.
 */
function MatchedCard({
  matches,
  matchRows,
  onRetry,
  shortlist,
  budgetMax,
  showMatches,
  showShortlist,
  path,
  plans,
}: {
  matches: DashboardPayload["matches"];
  matchRows: MatchRowsState;
  onRetry: () => void;
  shortlist: DashboardPayload["shortlist"];
  budgetMax: number | null;
  showMatches: boolean;
  showShortlist: boolean;
  path: (p: string) => string;
  plans: string;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"matches" | "shortlist">(showMatches ? "matches" : "shortlist");
  const initialIds = shortlist.ids ?? shortlist.items.map((i) => i.listing_id);
  const [savedIds, setSavedIds] = useState<Set<string>>(() => new Set(initialIds));
  const [saved, setSaved] = useState<OpportunityRow[]>(() =>
    shortlist.items.map((i) => ({
      listing_id: i.listing_id,
      name: i.name,
      category: i.category,
      location: i.city,
      image_url: i.image_url,
      asking: money(i.asking_price),
      revenue: i.revenue,
      asking_price: i.asking_price,
    })),
  );
  const [busyId, setBusyId] = useState<string | null>(null);

  // Scroll cue: the down arrow shows while more of the list sits below the fold.
  const listRef = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const measure = useCallback(() => {
    const el = listRef.current;
    setMoreBelow(!!el && el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);
  useEffect(measure, [measure, tab, matchRows, saved]);
  // The list's height follows the neighbouring card on lg, so re-measure on resize too.
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const matchesLocked = matches.locked || matchRows === "locked";
  const savedCount = shortlist.saved + (savedIds.size - initialIds.length);

  const toggleSaved = async (row: OpportunityRow) => {
    const id = row.listing_id;
    setBusyId(id);
    try {
      // Explicit add/remove, never a server toggle — same rule as the marketplace.
      const res = savedIds.has(id)
        ? await api.delete<{ favorited: boolean }>(`/favorites/${id}`)
        : await api.put<{ favorited: boolean }>(`/favorites/${id}`, {});
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (res.favorited) next.add(id);
        else next.delete(id);
        return next;
      });
      setSaved((prev) =>
        res.favorited
          ? prev.some((r) => r.listing_id === id) ? prev : [row, ...prev]
          : prev.filter((r) => r.listing_id !== id),
      );
    } catch {
      toast.error("Failed to update your shortlist");
    } finally {
      setBusyId(null);
    }
  };

  const heart = (row: OpportunityRow) => ({
    saved: savedIds.has(row.listing_id),
    busy: busyId === row.listing_id,
    // A locked shortlist (Basic) cannot save, so the heart sends them to plans.
    onHeart: shortlist.locked ? () => navigate(plans) : () => void toggleSaved(row),
  });

  const tabs = [
    showMatches && { key: "matches" as const, label: "Matches", count: Array.isArray(matchRows) ? matchRows.length : null },
    showShortlist && { key: "shortlist" as const, label: "Shortlist", count: shortlist.locked ? null : savedCount },
  ].filter(Boolean) as { key: "matches" | "shortlist"; label: string; count: number | null }[];

  let body: React.ReactNode;
  if (tab === "matches") {
    if (matchesLocked) {
      body = (
        <LockedNote to={plans}>
          Members get every active listing scored against their buybox on sector fit, price, geography and earnings.
        </LockedNote>
      );
    } else if (matchRows === "loading") {
      // Shimmer rows at the real row height, so the card doesn't resize when scores land.
      body = (
        <ul className="m-0 list-none p-0">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex gap-3 border-b border-border py-4 last:border-b-0">
              <span className="h-[6.5rem] w-[4.75rem] shrink-0 animate-pulse rounded-lg bg-muted" />
              <span className="flex-1">
                <span className="mb-2 block h-3 w-1/3 animate-pulse rounded bg-muted" />
                <span className="mb-2 block h-4 w-3/4 animate-pulse rounded bg-muted" />
                <span className="block h-3 w-1/2 animate-pulse rounded bg-muted" />
              </span>
            </li>
          ))}
        </ul>
      );
    } else if (matchRows === "error") {
      body = (
        <div className="flex flex-col items-start gap-3">
          <EmptyNote>We couldn't score your matches just now — that's on our side, not your buybox.</EmptyNote>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-gold-gradient px-3 py-2 text-sm font-semibold text-primary-foreground hover:brightness-[1.06]"
          >
            Try again
          </button>
        </div>
      );
    } else if (matchRows.length === 0) {
      body = (
        <EmptyNote>
          {matches.has_preferences
            ? "No active listings score against your buybox yet — widen a criterion or check back as new deals land."
            : "Set your buybox and we'll start scoring every new listing against it."}
        </EmptyNote>
      );
    } else {
      body = (
        <ul className="m-0 list-none p-0">
          {matchRows.map((m) => (
            <OpportunityItem key={m.listing_id} row={m} budgetMax={budgetMax} {...heart(m)} />
          ))}
        </ul>
      );
    }
  } else if (shortlist.locked) {
    body = <LockedNote to={plans}>Saving and comparing listings comes with any paid plan.</LockedNote>;
  } else if (saved.length === 0) {
    body = <EmptyNote>Nothing saved yet — tap the heart on any listing to keep it here.</EmptyNote>;
  } else {
    body = (
      <ul className="m-0 list-none p-0">
        {saved.map((r) => (
          <OpportunityItem key={r.listing_id} row={r} budgetMax={budgetMax} {...heart(r)} />
        ))}
      </ul>
    );
  }

  const footer =
    tab === "matches"
      ? !matchesLocked && Array.isArray(matchRows) && matchRows.length > 0
        ? { to: path("/buyer/deal-scoring"), label: "View all matched opportunities" }
        : null
      : !shortlist.locked && saved.length > 0
        ? { to: path("/buyer/favorites"), label: "Open full shortlist" }
        : null;

  return (
    <section className="flex min-w-0 flex-col rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="heading-medium m-0 text-[1.35rem] leading-tight">Matched to your Buybox</h2>
          <p className="m-0 mt-1.5 text-sm text-muted-foreground">A focused starting point for your search.</p>
        </div>
        <Link
          to={path("/buyer/settings/profile")}
          aria-label="Tune your buybox"
          title="Tune your buybox"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg hover:bg-accent"
        >
          <SlidersHorizontal className="h-[1.1rem] w-[1.1rem] text-primary" />
        </Link>
      </div>

      <div role="tablist" className="mb-4 mt-5 flex gap-6 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "-mb-px flex cursor-pointer items-center gap-2 border-b-2 pb-2.5 text-sm transition-colors",
              tab === t.key
                ? "border-primary font-semibold text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {t.count != null && (
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* On lg the card is stretched to the Buybox card beside it, so the list
          grows to fill that height instead of stopping at 26rem and leaving an
          empty band (with the scroll cue floating in it) above the footer. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={listRef}
          onScroll={measure}
          className="max-h-[26rem] overflow-y-auto pr-1 lg:min-h-[26rem] lg:max-h-none lg:flex-1 lg:basis-0"
        >
          {body}
        </div>
        {moreBelow && (
          <button
            type="button"
            aria-label="Scroll for more"
            onClick={() => listRef.current?.scrollBy({ top: 220, behavior: "smooth" })}
            className="absolute bottom-2 left-1/2 grid h-9 w-9 -translate-x-1/2 cursor-pointer place-items-center rounded-full border border-border bg-card shadow-card hover:border-primary/40"
          >
            <ArrowDown className="h-4 w-4 text-foreground" />
          </button>
        )}
      </div>

      {footer && (
        <div className="mt-3 border-t border-border pt-3 text-right">
          <GoldTextLink to={footer.to}>{footer.label}</GoldTextLink>
        </div>
      )}
    </section>
  );
}

/** "Up to $500K" / "$250K – $500K" / "$250K+" from the buybox's raw budget. */
function budgetLabel(lo?: number | null, hi?: number | null): string | null {
  if (hi && lo) return `${money(lo)} – ${money(hi)}`;
  if (hi) return `Up to ${money(hi)}`;
  if (lo) return `${money(lo)}+`;
  return null;
}

/** Buybox completion bar, the two headline criteria, and the buyer's next step. */
/** How many chips each Buy Box group previews before "+N more". The full
 *  lists (a buyer can target 100+ cities) live on the profile's Buy Box. */
const BUYBOX_PREVIEW = 4;

/** One Buy Box list on the dashboard card: label + count, the first few chips,
 *  and a "+N more" link through to the full Buy Box. */
function BuyboxChips({
  label,
  items,
  iconFor,
  to,
}: {
  label: string;
  items: string[];
  iconFor?: (item: string) => typeof Check;
  to: string;
}) {
  if (items.length === 0) return null;
  const rest = items.length - BUYBOX_PREVIEW;
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
        <span className="rounded-full bg-muted px-1.5 py-px text-[0.65rem] tabular-nums">{items.length.toLocaleString()}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.slice(0, BUYBOX_PREVIEW).map((item, i) => {
          const Icon = iconFor?.(item);
          return (
            <span
              key={`${item}-${i}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary/8 px-2.5 py-1 text-xs font-semibold text-foreground ring-1 ring-primary/20"
            >
              {Icon && <Icon className="h-3 w-3 shrink-0 text-primary" />}
              <span className="truncate">{item}</span>
            </span>
          );
        })}
        {rest > 0 && (
          <Link
            to={to}
            className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold no-underline hover:underline"
          >
            <span className="text-primary">+{rest.toLocaleString()} more</span>
          </Link>
        )}
      </div>
    </div>
  );
}

function BuyboxSummary({
  buybox,
  to,
  nextStep,
}: {
  buybox: DashboardPayload["buybox"];
  to: string;
  nextStep: { title: string; body: string; cta: string; to: string } | null;
}) {
  const budget = budgetLabel(buybox.budget_min, buybox.budget_max);
  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card">
      <div className="p-6">
        <div className="mb-2.5 flex items-baseline justify-between gap-3">
          <h2 className="heading-medium m-0 text-[1.2rem] leading-tight">Your Buybox</h2>
          <span className="text-base font-semibold tabular-nums text-primary">{buybox.completion}%</span>
        </div>
        <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${buybox.completion}%` }} />
        </div>
        <dl className="m-0 mb-5 grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-3 text-[15px]">
          <dt className="text-muted-foreground">Purchase budget</dt>
          <dd className="m-0 font-semibold text-foreground">
            {budget ?? <span className="font-normal text-muted-foreground">Not set</span>}
          </dd>
          {buybox.min_sde && (
            <>
              <dt className="text-muted-foreground">Minimum SDE</dt>
              <dd className="m-0 font-semibold text-foreground">{buybox.min_sde}</dd>
            </>
          )}
          {buybox.business_age && (
            <>
              <dt className="text-muted-foreground">Business age</dt>
              <dd className="m-0 font-semibold text-foreground">{buybox.business_age}</dd>
            </>
          )}
          <dt className="text-muted-foreground">Preferences</dt>
          <dd className="m-0">
            {buybox.has_preferences ? (
              <span className="inline-flex items-center gap-1.5 font-semibold text-success">
                <Check className="h-4 w-4" />
                Configured
              </span>
            ) : (
              <span className="text-muted-foreground">Not set</span>
            )}
          </dd>
        </dl>
        {buybox.has_preferences && (
          <div className="mb-5 space-y-4 border-t border-border pt-4">
            <BuyboxChips label="Industries" items={buybox.sectors ?? []} iconFor={industryIcon} to={to} />
            {(buybox.locations?.length ?? 0) > 0 ? (
              <BuyboxChips label="Locations" items={buybox.locations!} to={to} />
            ) : (buybox.countries?.length ?? 0) > 0 ? (
              <BuyboxChips label="Countries" items={buybox.countries!} to={to} />
            ) : (
              <>
                <BuyboxChips label="States" items={buybox.states ?? []} to={to} />
                <BuyboxChips label="Cities" items={buybox.cities ?? []} to={to} />
              </>
            )}
          </div>
        )}
        <Link
          to={to}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold no-underline hover:border-primary/40 hover:bg-accent/60"
        >
          <span className="text-foreground">{buybox.has_preferences ? "Review Buybox" : "Set up Buybox"}</span>
          <SlidersHorizontal className="h-4 w-4 text-foreground" />
        </Link>
      </div>

      {nextStep && (
        <div className="mt-auto border-t border-border bg-primary/10 p-6">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <ArrowUpRight className="h-3.5 w-3.5" />
            Your next step
          </div>
          <h3 className="heading-medium m-0 mb-2 text-[1.05rem] leading-snug">{nextStep.title}</h3>
          <p className="m-0 mb-4 text-sm leading-relaxed text-muted-foreground">{nextStep.body}</p>
          <Link
            to={nextStep.to}
            className="flex w-full items-center justify-between gap-2 rounded-lg bg-gold-gradient px-4 py-2.5 text-sm font-semibold text-primary-foreground no-underline hover:brightness-[1.06]"
          >
            {nextStep.cta}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </section>
  );
}


// ── rail panels ──────────────────────────────────────────────────────

/** The dark closure-forecast card at the top of the rail. */
function ForecastPanel({
  forecast,
  open,
  onToggle,
  to,
  upgradeTo,
}: {
  forecast: DashboardPayload["forecast"];
  open: boolean;
  onToggle: (id: string) => void;
  to: string;
  upgradeTo: string;
}) {
  return (
    // public-hero-shell carries the full Dark · Gold token set regardless of the
    // active theme (index.css), which is how every other always-dark block in
    // the app is built. The mockup's dark forecast card would otherwise have to
    // be `bg-foreground`, which inverts to a cream slab under the dark theme.
    <div className="public-hero-shell rounded-2xl border border-primary/25 p-5 text-foreground">
      <button type="button" onClick={() => onToggle("forecast")} aria-expanded={open} className="w-full cursor-pointer text-left">
        <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.16em] text-primary">Closure forecast</div>
        {forecast.locked ? (
          <p className="m-0 text-sm leading-relaxed text-muted-foreground">
            The Operator plan adds a running likelihood-to-close on your lead deal, read off your Deal CRM
            board, with the risks behind the number.
          </p>
        ) : !forecast.has_deal ? (
          <p className="m-0 text-sm leading-relaxed text-muted-foreground">
            Nothing in the pipeline to forecast yet. Add a deal to the CRM and this starts tracking its odds.
          </p>
        ) : (
          <>
            <div className="flex items-end gap-3">
              <div className="font-display text-[3.875rem] font-normal leading-[0.9] tabular-nums">
                {forecast.percent}
                <span className="text-[1.625rem]">%</span>
              </div>
              <div className="pb-1.5 text-sm leading-snug text-muted-foreground">
                likelihood {forecast.deal_name_short ?? forecast.deal_name} closes
              {forecast.stage_label ? ` — now at ${forecast.stage_label}` : ""}
              </div>
            </div>
            <div className="my-3 h-px bg-border" />
            <div className="flex flex-col gap-2.5">
              {forecast.risks.map((r) => (
                <div key={r.title} className="flex items-baseline gap-2.5">
                  <span className={cn("h-[7px] w-[7px] shrink-0 rounded-full", RISK_DOT[r.level])} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">{r.title}</span>
                    <span className="block text-xs text-muted-foreground">{r.note}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </button>

      {/* Outside the toggle button: an anchor nested in a <button> is invalid. */}
      {forecast.locked && (
        <div className="mt-3">
          <CardLink to={upgradeTo}>Upgrade to Operator</CardLink>
        </div>
      )}

      {open && forecast.has_deal ? (
        <div className="mt-4 border-t border-border pt-3">
          {forecast.stalled.length > 0 && (
            <>
              <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.16em] text-primary">Stalled items</div>
              <div className="mb-3 flex flex-col gap-1.5">
                {forecast.stalled.map((s) => (
                  <Link
                    key={s.listing_id}
                    to={`/listings/${s.listing_id}`}
                    className="group flex justify-between gap-3 text-sm"
                  >
                    <span className="min-w-0 truncate text-muted-foreground group-hover:text-primary">{s.item}</span>
                    <span className="shrink-0 font-mono tabular-nums text-muted-foreground">{s.age}</span>
                  </Link>
                ))}
              </div>
            </>
          )}
          <Link
            to={to}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary px-3 py-2 text-sm font-semibold hover:bg-primary/10"
          >
            <span className="text-primary">Open the deal CRM</span>
            <ArrowRight className="h-3.5 w-3.5 text-primary" />
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Niva's launcher on the dashboard rail.
 *
 * Deliberately NOT a second chat: the app-wide drawer (ChatbotBubble) owns the
 * transcript, its persistence, the listing cards a reply can carry and the tier
 * gating around all of it. This panel hands a question to that drawer through
 * NivaContext.askNiva and lets it answer there, so the buyer has one
 * conversation with Niva rather than two that don't know about each other.
 */
function NivaPanel({
  entitled,
  upgradeTo,
  buyboxTo,
}: {
  entitled: boolean;
  upgradeTo: string;
  /** "Refine your buybox" chip — opens the BuyBox editor rather than asking Niva. */
  buyboxTo: string;
}) {
  const { available, openNiva, askNiva } = useNiva();
  const [input, setInput] = useState("");

  const ask = useCallback(
    (question: string) => {
      const text = question.trim();
      if (!text) return;
      askNiva(text);
      setInput("");
    },
    [askNiva],
  );

  // `available` is the platform kill switch plus "not a free buyer"; `entitled`
  // is whether this viewer may actually send. Off entirely → render nothing
  // rather than a dead card.
  if (!available) return null;

  const prompts = [
    "Summarise my diligence gaps",
    "What should I do next?",
    "Explain SDE vs EBITDA",
  ];

  return (
    <div className="relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-card">
      {/* Her portrait sits top-right, as on the reference; the copy wraps beside it. */}
      <img
        src={NIVA_AVATAR_POSTER}
        alt=""
        aria-hidden
        className="absolute right-0 top-0 h-[7.5rem] w-[7.5rem] object-cover object-top [mask-image:linear-gradient(to_left,black_70%,transparent)]"
      />
      <div className="relative pr-[7rem]">
        <span className="flex items-center gap-2 heading-medium font-display text-[1.15rem] text-foreground">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" />
          Ask Niva AI Assistant
        </span>
        <p className="m-0 mt-2 text-sm leading-relaxed text-muted-foreground">
          {entitled
            ? "Ask about a listing, your buybox, or what to do next on a deal — she answers in the chat panel."
            : "Niva reads your buybox and your pipeline to answer questions about a deal. She comes with the Operator plan."}
        </p>
      </div>

      {!entitled ? (
        <div className="relative mt-4">
          <CardLink to={upgradeTo}>See plans</CardLink>
        </div>
      ) : (
        <>
          <div className="relative mt-4 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ask(input); }}
              placeholder="Ask Niva about a deal…"
              aria-label="Ask Niva"
              className="h-11 min-w-0 flex-1 rounded-xl border border-border bg-transparent px-3.5 text-sm outline-none focus-visible:border-primary"
            />
            <button
              type="button"
              onClick={() => (input.trim() ? ask(input) : openNiva())}
              aria-label="Ask"
              className="grid h-11 w-11 shrink-0 cursor-pointer place-items-center rounded-xl bg-gold-gradient text-primary-foreground hover:brightness-[1.06]"
            >
              <ArrowUpRight className="h-5 w-5" />
            </button>
          </div>

          <div className="relative mt-3 flex flex-wrap gap-2">
            {prompts.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => ask(p)}
                className="cursor-pointer rounded-lg border border-border px-3 py-1.5 text-xs text-foreground hover:border-primary/40 hover:bg-accent/60"
              >
                {p}
              </button>
            ))}
            <Link
              to={buyboxTo}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs no-underline hover:border-primary/40 hover:bg-accent/60"
            >
              <span className="text-foreground">Refine your buybox</span>
              <SlidersHorizontal className="h-3 w-3 text-primary" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

/** Straight lines into the modules the cards above summarise.
 *
 * `module` mirrors the sidebar's own keys, so a switched-off module disappears
 * from both at once — a quick link is exactly as dead as the sidebar entry it
 * duplicates. Entries with no key (the marketplace, settings) are always shown.
 *
 * `member` marks the ones a Basic buyer cannot open. Those are NOT hidden: they
 * keep their place with a padlock and route to the plan page instead, which
 * prompts the upgrade rather than either dead-ending on a 402 or quietly
 * shrinking the rail so a Basic buyer never learns the feature exists.
 *
 * `operator` marks the one a paid Explorer buyer cannot open either (the Deal
 * CRM's route is MembersOnlyGate requires="operator"). Whether it is walled
 * comes from the payload's `pipeline.locked`, so the rail and the CRM card can
 * never disagree about it.
 */
const QUICK_LINKS: { to: string; label: string; icon: LucideIcon; module?: string; member?: boolean; operator?: boolean }[] = [
  { to: "/buyer/home", label: "Browse listings", icon: Target },
  { to: "/buyer/deal-scoring", label: "AI deal matching", icon: Sparkles, module: "ai_deal_matching", member: true },
  { to: "/buyer/pipeline", label: "Deal CRM", icon: KanbanSquare, module: "deal_crm", member: true, operator: true },
  { to: "/buyer/favorites", label: "Shortlist", icon: Heart, module: "favorites", member: true },
  { to: "/buyer/interests", label: "Messages", icon: MessageSquare, module: "interests", member: true },
  { to: "/buyer/funding", label: "Financing", icon: Landmark, module: "funding_marketplace", member: true },
  { to: "/buyer/sba-calculator", label: "SBA calculator", icon: TrendingUp, module: "sba_calculator", member: true },
  { to: "/buyer/financial-model", label: "Financial model", icon: ChartColumn, module: "financial_model", member: true },
  { to: "/buyer/settings/documents", label: "Documents", icon: FileText },
  { to: "/academy", label: "BL Academy", icon: GraduationCap, module: "bl_academy", member: true },
  { to: "/buyer/upcoming-modules", label: "Upcoming modules", icon: CalendarClock, module: "upcoming_modules", member: true },
];

function QuickLinks({
  path,
  isBasic,
  plans,
  crmLocked,
  operatorPlans,
}: {
  path: (p: string) => string;
  isBasic: boolean;
  plans: string;
  crmLocked: boolean;
  operatorPlans: string;
}) {
  const isModuleVisible = useModuleVisibility();
  const links = QUICK_LINKS.filter((l) => isModuleVisible(l.module));
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <Kicker className="mb-3">Jump to</Kicker>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {links.map(({ to, label, icon: Icon, member, operator }) => {
          const operatorWalled = !!operator && crmLocked;
          const walled = (isBasic && member) || operatorWalled;
          return (
            <Link
              key={to}
              to={walled ? (operatorWalled ? operatorPlans : plans) : path(to)}
              title={walled ? `${label} — upgrade to unlock` : undefined}
              className="group flex items-center gap-2.5 rounded-lg px-1.5 py-2 text-sm no-underline hover:bg-accent/60"
            >
              <Icon className={cn("h-4 w-4 shrink-0", walled ? "text-muted-foreground" : "text-primary")} />
              <span
                className={cn(
                  "truncate group-hover:text-primary",
                  walled ? "text-muted-foreground" : "text-foreground",
                )}
              >
                {label}
              </span>
              {walled ? (
                <Lock className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-primary" />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
